import { useEffect, useRef, useState } from 'react'
import { Editor } from '@tiptap/react'

interface AiCommandManagerProps {
  editor: Editor
  completeResolver?: (
    commandName: string,
    editorContent: string,
    selection: string,
    selectedText: string,
    inputValue: string
  ) => Promise<string | ReadableStream<Uint8Array<ArrayBufferLike>>>
}

export function AiCommandManager({ editor, completeResolver }: AiCommandManagerProps) {
  const [isProcessing, setIsProcessing] = useState(false)
  const [, setCompletion] = useState('')
  const processingRef = useRef(false)

  useEffect(() => {
    if (!editor || !completeResolver) return

    const handleOpenAiCommand = async ({ commandName }: { commandName: string }) => {
      // Prevent multiple simultaneous processing
      if (processingRef.current) return
      processingRef.current = true
      setIsProcessing(true)
      
      try {
        // Get the entire document content for summary/FAQ generation
        const editorContent = editor.getJSON()
        const selection = editor.state.selection
        const fullDocContent = editor.storage.markdown.serializer.serialize(editor.state.doc)
        
        // For document-level commands like summary and FAQ, use full doc content
        const contentToProcess = ['generateSummary', 'generateFaq'].includes(commandName) 
          ? fullDocContent 
          : editor.storage.markdown.serializer.serialize(selection.content().content)
        
        console.log('Executing AI command:', commandName)
        console.log('Content to process length:', contentToProcess.length)
        
        const result = await completeResolver(
          commandName,
          JSON.stringify(editorContent),
          JSON.stringify(selection),
          contentToProcess,
          '' // No additional input needed for these commands
        )
        
        let finalContent = ''
        if (typeof result === 'string') {
          finalContent = result
        } else {
          // Convert stream to string
          const reader = result.getReader()
          let chunks = ''
          while (true) {
            const { done, value } = await reader.read()
            if (done) break
            chunks += new TextDecoder().decode(value)
          }
          finalContent = chunks
        }
        
        console.log('Generated content:', finalContent)

        // Insert the generated content at the current cursor position
        if (finalContent) {
          const { to } = editor.state.selection

          // Handle FAQ generation - expects TipTap JSON from backend
          if (commandName === 'generateFaq') {
            try {
              // Extract JSON from the response (might be wrapped in markdown code blocks)
              let jsonContent = finalContent.trim()

              // Remove markdown code block if present
              if (jsonContent.startsWith('```json')) {
                jsonContent = jsonContent.replace(/^```json\n?/, '').replace(/\n?```$/, '')
              } else if (jsonContent.startsWith('```')) {
                jsonContent = jsonContent.replace(/^```\n?/, '').replace(/\n?```$/, '')
              }

              // Parse the TipTap JSON response
              const faqDoc = JSON.parse(jsonContent)
              console.log('Parsed FAQ document:', faqDoc)

              // Insert the TipTap JSON document content directly
              if (faqDoc && faqDoc.content && Array.isArray(faqDoc.content)) {
                // Insert each content node separately to ensure proper rendering
                editor.chain()
                  .focus()
                  .insertContentAt(to, faqDoc.content)
                  .run()
                return
              } else if (faqDoc && faqDoc.type === 'doc' && faqDoc.content) {
                // Handle if the response includes the full doc wrapper
                editor.chain()
                  .focus()
                  .insertContentAt(to, faqDoc.content)
                  .run()
                return
              } else {
                // Try to insert the parsed content directly if it's in the right format
                editor.chain()
                  .focus()
                  .insertContentAt(to, faqDoc)
                  .run()
                return
              }
            } catch (error) {
              console.log('Failed to parse FAQ as TipTap JSON, falling back to markdown parsing', error)
              console.log('Raw content that failed to parse:', finalContent)
              // Fall through to markdown parsing as fallback
            }
          }

          // Handle Summary generation and fallback for FAQ
          let titleContent = ''
          if (commandName === 'generateSummary') {
            titleContent = '## Summary\n\n'
          } else if (commandName === 'generateFaq') {
            titleContent = '## Frequently Asked Questions\n\n'
          }

          const fullContent = titleContent + finalContent

          // Try to parse as markdown first
          let parsedDoc
          try {
            if (editor.storage.markdown && editor.storage.markdown.parser) {
              parsedDoc = editor.storage.markdown.parser.parse(fullContent)
              if (parsedDoc && parsedDoc.content && parsedDoc.content.size > 0) {
                // Successfully parsed markdown to TipTap document
                console.log('Successfully parsed markdown to TipTap document')
              }
            }
          } catch (error) {
            console.log('Failed to parse as markdown, will use fallback', error)
          }

          // Insert the content
          if (parsedDoc) {
            // Insert parsed markdown document directly
            editor.chain()
              .focus()
              .insertContentAt(to, parsedDoc)
              .run()
          } else {
            // Fallback: Split by newlines and create proper paragraph structure
            const lines = fullContent.split('\n').filter(line => line.trim())
            const paragraphs = lines.map(line => ({
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  text: line
                }
              ]
            }))

            editor.chain()
              .focus()
              .insertContentAt(to, paragraphs)
              .run()
          }
        }
        
        setCompletion(finalContent)
      } catch (error) {
        console.error('Error executing AI command:', error)
      } finally {
        setIsProcessing(false)
        processingRef.current = false
        setCompletion('')
      }
    }

    editor.on('openAiCommand', handleOpenAiCommand)

    return () => {
      editor.off('openAiCommand', handleOpenAiCommand)
    }
  }, [editor, completeResolver])

  // Show loading indicator when processing
  if (isProcessing) {
    return (
      <div className="fixed bottom-4 right-4 z-50 bg-background border rounded-lg p-4 shadow-lg">
        <div className="flex items-center gap-2">
          <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
          <span className="text-sm">Generating content...</span>
        </div>
      </div>
    )
  }

  return null
}